import { useEffect, useRef, useState } from "react";

/* useCountdown — a LIVE, ticking countdown to an ISO timestamp (the daily-reward
   next_claim_at). Returns { ms, text, done }:
     • ms   — milliseconds remaining (clamped at 0)
     • text — compact human string: "23h 45m 12s" / "45m 12s" / "12s"
     • done — true once the target passes (caller refetches claim state)
   Shared by every daily-reward surface so the countdown that replaces the Claim
   button reads identically on Home, the Casino card, and the Rewards pages.
   Ticks once per second only while a future target exists (cleaned up on unmount).
   onDone fires once, at the 0 crossing, so the card can re-check can_claim. */
export function formatCountdown(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

export function useCountdown(iso, onDone) {
  const [now, setNow] = useState(() => Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
    if (!iso) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [iso]);

  const target = iso ? new Date(iso).getTime() : 0;
  const ms = iso ? Math.max(0, target - now) : 0;

  useEffect(() => {
    if (iso && ms <= 0 && !firedRef.current) {
      firedRef.current = true;
      onDone?.();
    }
  }, [iso, ms, onDone]);

  return { ms, text: formatCountdown(ms), done: iso ? ms <= 0 : true };
}
